/**
 * DBN Technical Assistance Manager
 */
import React, { useState, useEffect } from "react";
import { dbnApi, PageHeader, SectionHeader, Badge, LoadingState, Btn, ProgressBar, Modal, scoreColor, fmtEur } from "./dbnUtils";
import { GraduationCap, Plus, AlertTriangle, CheckCircle, TrendingUp, Users, BookOpen, Wrench } from "lucide-react";

const TYPE_COLORS = { Training:"#60a5fa", Advisory:"#34d399", Technical:"#a78bfa" };
const STATUS_COLORS = { "Completed":"#22c55e", "In Progress":"#3b82f6", "Completing":"#0d9488", "Planned":"#f59e0b" };

function TACard({ ta, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(ta.completion_pct);
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const newStatus = pct >= 100 ? "Completed" : pct >= 80 ? "Completing" : "In Progress";
      const outcome = pct >= 80 ? "Improved" : "Pending";
      await dbnApi.updateTa(ta.id, { completion_pct: pct, status: newStatus, outcome });
      onUpdate();
    } catch(e) {}
    setUpdating(false);
    setOpen(false);
  };

  const tc = TYPE_COLORS[ta.type] || "#60a5fa";
  const sc = STATUS_COLORS[ta.status] || "#475569";

  return (
    <>
      <div style={{ background:"#060f1e", border:"1px solid #0f2033", borderRadius:12, padding:"12px 14px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
          <div style={{ flex:1 }}>
            <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff", marginBottom:3 }}>{ta.name}</p>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
              <Badge label={ta.type} color={tc} />
              <Badge label={ta.status} color={sc} />
              <Badge label={ta.beneficiary_type} color="#475569" />
              {ta.outcome === "Improved" && <Badge label="Improved" color="#22c55e" />}
            </div>
          </div>
          <div style={{ textAlign:"right", flexShrink:0 }}>
            <p style={{ fontSize:14, fontWeight:900, color:"#34d399" }}>EUR {(ta.budget/1000).toFixed(0)}K</p>
            <p style={{ fontSize:8, color:"#334155" }}>{ta.funded_by}</p>
          </div>
        </div>

        <p style={{ fontSize:9, color:"#475569", marginBottom:4 }}>
          Gap: <span style={{ color:"#64748b" }}>{ta.gap}</span>
        </p>
        <p style={{ fontSize:9, color:"#475569", marginBottom:8 }}>
          Provider: <span style={{ color:"#94a3b8", fontWeight:600 }}>{ta.provider}</span>
          &nbsp;·&nbsp; {ta.start_date} → {ta.end_date}
        </p>

        <div style={{ marginBottom:8 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
            <span style={{ fontSize:9, color:"#475569" }}>Completion</span>
            <span style={{ fontSize:10, fontWeight:700, color:scoreColor(ta.completion_pct) }}>
              {ta.completion_pct}%
            </span>
          </div>
          <ProgressBar value={ta.completion_pct} color={sc} height={5} showPct={false} />
        </div>

        {ta.evidence && (
          <p style={{ fontSize:9, color:"#334155", marginBottom:6 }}>Evidence: {ta.evidence}</p>
        )}

        <Btn small outline color="#475569" onClick={() => setOpen(true)}>Update Progress</Btn>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={`Update: ${ta.name}`} width={420}>
        <p style={{ fontSize:10, color:"#475569", marginBottom:12 }}>{ta.intervention}</p>
        <div style={{ marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <label style={{ fontSize:10, color:"#64748b", fontWeight:600 }}>Completion: {pct}%</label>
          </div>
          <input type="range" min={0} max={100} step={5} value={pct}
            onChange={e => setPct(+e.target.value)}
            style={{ width:"100%", accentColor:"#34d399" }} />
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <Btn outline color="#475569" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn color="#34d399" onClick={handleUpdate} disabled={updating}>
            {updating ? "Saving…" : "Save Update"}
          </Btn>
        </div>
      </Modal>
    </>
  );
}

function NewTAForm({ pfis, onCreated }) {
  const [form, setForm] = useState({
    name:"", type:"Training", beneficiary_type:"PFI", beneficiary_id:"",
    gap:"", intervention:"", provider:"", budget:"", funded_by:"FAC-EIB",
    start_date:"2025-01-01", end_date:"2025-12-31"
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await dbnApi.createTa({ ...form, budget: parseFloat(form.budget)||0 });
      setDone(true);
      onCreated();
    } catch(e) {}
    setSaving(false);
  };

  if (done) return (
    <div style={{ textAlign:"center", padding:"24px 0" }}>
      <CheckCircle size={40} color="#34d399" style={{ margin:"0 auto 12px", display:"block" }} />
      <p style={{ fontSize:14, fontWeight:700, color:"#f0f9ff", marginBottom:6 }}>TA Programme Created</p>
      <p style={{ fontSize:10, color:"#64748b", marginBottom:16 }}>The programme has been added to the active TA portfolio.</p>
      <Btn onClick={() => setDone(false)} color="#34d399" outline>Create Another</Btn>
    </div>
  );

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
      {[
        {key:"name", label:"Programme Name", type:"text", span:2},
        {key:"provider", label:"TA Provider", type:"text"},
        {key:"budget", label:"Budget (EUR)", type:"number"},
        {key:"gap", label:"Capability Gap Addressed", type:"text", span:2},
        {key:"intervention", label:"Intervention Description", type:"text", span:2},
      ].map(f => (
        <div key={f.key} style={{ gridColumn:f.span===2?"span 2":"auto" }}>
          <label style={{ fontSize:10, color:"#64748b", display:"block", marginBottom:4, fontWeight:600 }}>{f.label}</label>
          <input type={f.type} value={form[f.key]} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))}
            style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:8,
              padding:"8px 12px", fontSize:11, color:"#f0f9ff", boxSizing:"border-box" }} />
        </div>
      ))}
      {[
        {key:"type", label:"TA Type", opts:["Training","Advisory","Technical"]},
        {key:"beneficiary_type", label:"Beneficiary Type", opts:["PFI","Project","MSME"]},
        {key:"funded_by", label:"Funded By", opts:["FAC-EIB","FAC-AFDB","FAC-KFW","FAC-WB"]},
      ].map(f => (
        <div key={f.key}>
          <label style={{ fontSize:10, color:"#64748b", display:"block", marginBottom:4, fontWeight:600 }}>{f.label}</label>
          <select value={form[f.key]} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))}
            style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:8,
              padding:"8px 12px", fontSize:11, color:"#f0f9ff" }}>
            {f.opts.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      ))}
      <div>
        <label style={{ fontSize:10, color:"#64748b", display:"block", marginBottom:4, fontWeight:600 }}>Beneficiary</label>
        <select value={form.beneficiary_id} onChange={e => setForm(p=>({...p,beneficiary_id:e.target.value}))}
          style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:8,
            padding:"8px 12px", fontSize:11, color:"#f0f9ff" }}>
          <option value="">Select PFI…</option>
          {pfis.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <label style={{ fontSize:10, color:"#64748b", display:"block", marginBottom:4, fontWeight:600 }}>Start Date</label>
        <input type="date" value={form.start_date} onChange={e=>setForm(p=>({...p,start_date:e.target.value}))}
          style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:8,
            padding:"8px 12px", fontSize:11, color:"#f0f9ff" }} />
      </div>
      <div>
        <label style={{ fontSize:10, color:"#64748b", display:"block", marginBottom:4, fontWeight:600 }}>End Date</label>
        <input type="date" value={form.end_date} onChange={e=>setForm(p=>({...p,end_date:e.target.value}))}
          style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:8,
            padding:"8px 12px", fontSize:11, color:"#f0f9ff" }} />
      </div>
      <div style={{ gridColumn:"span 2", display:"flex", justifyContent:"flex-end", marginTop:4 }}>
        <Btn color="#34d399" onClick={handleCreate} disabled={saving || !form.name}>
          {saving ? "Creating…" : "Create TA Programme"}
        </Btn>
      </div>
    </div>
  );
}

export default function DBNTAManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [pfis, setPfis] = useState([]);

  const load = () => {
    Promise.all([dbnApi.taManager(), dbnApi.pfis()])
      .then(([r, rp]) => { setData(r.data); setPfis(rp.data?.pfis||[]); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); }, []);

  if (loading) return <LoadingState />;
  const s = data?.summary || {};
  const programmes = data?.programmes || [];
  const gaps = data?.pfi_gap_matrix || [];

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="Technical Assistance"
        subtitle="Capability gap diagnosis · TA programme tracking · Impact measurement">
        <Btn small color="#34d399" onClick={() => setTab("new")}>+ New Programme</Btn>
      </PageHeader>

      <div style={{ padding:"16px 24px", flex:1 }}>
        {/* Summary Stats */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10, marginBottom:16 }}>
          {[
            { label:"Total Programmes",  value:s.total_programmes, color:"#60a5fa", icon:BookOpen  },
            { label:"Active",            value:s.active,           color:"#34d399", icon:TrendingUp },
            { label:"Completed",         value:s.completed,        color:"#22c55e", icon:CheckCircle},
            { label:"PFIs Covered",      value:s.pfis_covered,     color:"#a78bfa", icon:Users     },
            { label:"Improved Outcomes", value:s.improved_outcome, color:"#f59e0b", icon:TrendingUp },
            { label:"Total Budget",      value:`€${(s.total_budget/1000).toFixed(0)}K`, color:"#f97316", icon:GraduationCap },
          ].map(x => (
            <div key={x.label} style={{ background:"#0a1628", border:"1px solid #0f2033",
              borderTop:`3px solid ${x.color}`, borderRadius:12, padding:"12px 14px" }}>
              <p style={{ fontSize:x.label==="Total Budget"?14:22, fontWeight:900, color:x.color }}>{x.value}</p>
              <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>{x.label}</p>
            </div>
          ))}
        </div>

        {/* Avg completion progress */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:"12px 16px", marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontSize:11, color:"#94a3b8", fontWeight:600 }}>Portfolio TA Completion</span>
            <span style={{ fontSize:12, fontWeight:900, color:scoreColor(s.avg_completion) }}>{s.avg_completion}%</span>
          </div>
          <ProgressBar value={s.avg_completion} color="#34d399" height={7} showPct={false} />
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:2, marginBottom:16, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:10, padding:4, width:"fit-content" }}>
          {[
            {key:"active", label:"Active Programmes"},
            {key:"gaps",   label:"Gap Diagnosis"},
            {key:"budget", label:"Budget Allocation"},
            {key:"new",    label:"New Programme"},
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                background: tab===t.key ? "rgba(52,211,153,0.15)" : "transparent",
                color: tab===t.key ? "#34d399" : "#475569",
              }}>{t.label}</button>
          ))}
        </div>

        {/* Active Programmes */}
        {tab === "active" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {programmes.map(ta => (
              <TACard key={ta.id} ta={ta} onUpdate={load} />
            ))}
          </div>
        )}

        {/* Gap Diagnosis */}
        {tab === "gaps" && (
          <div>
            <p style={{ fontSize:11, color:"#64748b", marginBottom:14 }}>
              PFI capability gaps identified through scoring analysis. PFIs with most gaps are prioritised for TA.
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {gaps.map(p => (
                <div key={p.id} style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:"12px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                    <div>
                      <p style={{ fontSize:12, fontWeight:700, color:"#f0f9ff" }}>{p.name}</p>
                      <p style={{ fontSize:9, color:"#475569" }}>{p.type} · Tier {p.tier}</p>
                    </div>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      {p.has_active_ta && <Badge label="TA Active" color="#34d399" />}
                      <span style={{ fontSize:11, fontWeight:700, color: p.gap_count>=3?"#ef4444":p.gap_count>=1?"#f59e0b":"#22c55e" }}>
                        {p.gap_count} gap{p.gap_count!==1?"s":""}
                      </span>
                    </div>
                  </div>
                  {p.gaps.length > 0 ? (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:8 }}>
                      {p.gaps.map(g => (
                        <div key={g} style={{ display:"flex", alignItems:"center", gap:4,
                          background:"#1a1a2e", borderRadius:6, padding:"3px 8px" }}>
                          <AlertTriangle size={8} color="#f59e0b" />
                          <span style={{ fontSize:9, color:"#f59e0b" }}>{g}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Badge label="No gaps identified" color="#22c55e" />
                  )}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:6 }}>
                    {[
                      {l:"Green",       v:p.green_score},
                      {l:"ESG",         v:p.esg_score},
                      {l:"Data",        v:p.data_quality},
                      {l:"Reporting",   v:p.reporting},
                      {l:"Readiness",   v:p.readiness},
                    ].map(x => (
                      <div key={x.l} style={{ textAlign:"center" }}>
                        <p style={{ fontSize:11, fontWeight:700, color:scoreColor(x.v) }}>{x.v}</p>
                        <p style={{ fontSize:8, color:"#334155" }}>{x.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Budget Allocation */}
        {tab === "budget" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Budget by TA Type" sub="EUR allocated per intervention type" />
              {Object.entries(data?.budget_by_type||{}).map(([type, amt]) => (
                <div key={type} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:10, color:"#94a3b8" }}>{type}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:TYPE_COLORS[type]||"#60a5fa" }}>EUR {(amt/1000).toFixed(0)}K</span>
                  </div>
                  <ProgressBar value={amt} max={s.total_budget} color={TYPE_COLORS[type]||"#60a5fa"} height={6} showPct={true} />
                </div>
              ))}
            </div>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Budget by Funder" sub="EUR committed per development partner" />
              {Object.entries(data?.budget_by_funder||{}).map(([funder, amt]) => {
                const colors = {"FAC-EIB":"#3b82f6","FAC-AFDB":"#34d399","FAC-KFW":"#f59e0b","FAC-WB":"#a78bfa"};
                return (
                  <div key={funder} style={{ marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:10, color:"#94a3b8" }}>{funder.replace("FAC-","")}</span>
                      <span style={{ fontSize:10, fontWeight:700, color:colors[funder]||"#60a5fa" }}>EUR {(amt/1000).toFixed(0)}K</span>
                    </div>
                    <ProgressBar value={amt} max={s.total_budget} color={colors[funder]||"#60a5fa"} height={6} showPct={true} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* New Programme */}
        {tab === "new" && (
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:20 }}>
            <SectionHeader title="Create New TA Programme" sub="Fill in the programme details below" />
            <NewTAForm pfis={pfis} onCreated={load} />
          </div>
        )}
      </div>
    </div>
  );
}
